import { isAxiosError } from 'axios'
import type { CampaignUploadFailureReason } from '@/domain/campaign-ports'

// Step-aware failure mapping for the Campaign upload protocol. Kept beside the
// upload adapter rather than folded into `mapCampaignHttpError`: that mapper
// produces a `CampaignError` from the Campaign `{ error: { code, message } }`
// envelope, while these steps must produce a domain `reason` and — for the
// direct object-storage PUT — read a response object storage returns, not Atlas.
//
// Every message fragment matched below is an exact, source-verified string the
// file-service emits. They are the only discriminators available: the shared
// envelope collapses several distinct conditions onto one `VALIDATION_ERROR`
// code and one `400`. Where no reliable discriminator exists, the mapping falls
// back to the generic reason rather than guessing at ambiguous wording.

const statusOf = (error: unknown): number | undefined => (isAxiosError(error) ? error.response?.status : undefined)

/** `Content type '<x>' is not allowed. Allowed: …` — the create path's declared-type rejection. */
const DECLARED_TYPE_REJECTION = 'is not allowed'
/** `File size <n> exceeds maximum <m> bytes` — confirm's server-side size check. */
const CONFIRM_OVERSIZE = 'exceeds maximum'
/** `File content (detected: …) does not match declared type (…)` — confirm's magic-byte check. */
const CONFIRM_CONTENT_MISMATCH = 'does not match declared type'
/** Object storage's identifiers for an object that was never written — the PUT never landed. */
const MISSING_OBJECT_SIGNALS = ['nosuchkey', 'not found']
/** Object storage's concrete signature-expiry rejection, distinct from a generic denial. */
const SIGNATURE_EXPIRY_SIGNALS = ['request has expired', 'expiredtoken']

/** Lower-cased backend message from the Atlas `{ error: { message } }` envelope, or `''`. */
function messageOf(error: unknown): string {
	if (!isAxiosError(error)) return ''
	const data = error.response?.data as { error?: { message?: unknown }; message?: unknown } | undefined
	const message = data?.error?.message ?? data?.message
	return typeof message === 'string' ? message.toLowerCase() : ''
}

/** A failure before the transport was ready to send bytes — File creation or the presign. */
export function mapUploadInitiationFailure(error: unknown): CampaignUploadFailureReason {
	const status = statusOf(error)
	if (status === 400) return messageOf(error).includes(DECLARED_TYPE_REJECTION) ? 'invalid-file-type' : 'failure'
	if (status === 401 || status === 403) return 'forbidden'
	if (status === 500 || status === 503) return 'unavailable'
	return 'failure'
}

/**
 * The direct object-storage `PUT`. Object storage returns a raw XML body, never
 * the Atlas envelope, so only its own concrete signature-expiry wording is
 * distinguished; every other rejection and every network interruption maps to a
 * generic transfer failure. An aborted request lands here too and is discarded
 * by the caller rather than reported.
 */
export function mapUploadTransferFailure(error: unknown): CampaignUploadFailureReason {
	const body = isAxiosError(error) && typeof error.response?.data === 'string' ? error.response.data.toLowerCase() : ''
	return SIGNATURE_EXPIRY_SIGNALS.some((signal) => body.includes(signal)) ? 'expired-upload-url' : 'transfer-failed'
}

/** `POST /files/{fileId}/confirm-upload`. */
export function mapUploadConfirmationFailure(error: unknown): CampaignUploadFailureReason {
	const status = statusOf(error)
	const message = messageOf(error)
	// A `404` is the File RECORD being absent, not the uploaded object — checked
	// before the missing-object signals, whose own wording would otherwise match it.
	if (status === 404) return 'failure'
	if (MISSING_OBJECT_SIGNALS.some((signal) => message.includes(signal))) return 'transfer-failed'
	if (status === 400) {
		if (message.includes(CONFIRM_OVERSIZE)) return 'file-too-large'
		if (message.includes(CONFIRM_CONTENT_MISMATCH)) return 'content-rejected'
		return 'failure'
	}
	if (status === 500 || status === 503) return 'unavailable'
	return 'failure'
}

/** `POST /classify/batch`. An unknown axis or value is indistinguishable from any other failure. */
export function mapUploadClassificationFailure(error: unknown): CampaignUploadFailureReason {
	const status = statusOf(error)
	if (status === 401 || status === 403) return 'forbidden'
	if (status === 500 || status === 503) return 'unavailable'
	return 'failure'
}
